import React, { useState, useEffect, useRef } from "react";
import { useStudioEditor } from '../../../store/studioEditorStore';
import ErrorMessage from "../../StudioErstellen/ErrorMessage";
import classes from "../../StudioErstellen/Standort.module.css";
import GermanyFlag from "../../../public/icons/germanEmoji.svg";
import { 
    validateAddressFields, 
    validateAddressWithGoogle 
} from "../../../util/addressValidation";
import { useAddressAutocomplete } from "../../../hooks/useAddressAutocomplete";

export const StandortEditor = ({ studio, validate }) => {
    const { updateField, updatedFields } = useStudioEditor();
    const [errors, setErrors] = useState({
        country: "",
        street: "",
        postalCode: "",
        city: "",
        general: ""
    });
    const [isValidating, setIsValidating] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [streetInputValue, setStreetInputValue] = useState(studio?.streetAndNumber || "");
    const addressInputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const hasAddressChangesRef = useRef(false);

    const { suggestions, isLoading, searchAddress, selectPlace, clearSuggestions } = useAddressAutocomplete();
    const hasUpdatedField = (fieldName) => Object.prototype.hasOwnProperty.call(updatedFields, fieldName);
    const getUpdatedField = (fieldName, fallback) => (
        hasUpdatedField(fieldName) ? updatedFields[fieldName] : fallback
    );

    // Extract street from address if available
    const extractStreetFromAddress = (address) => {
        if (!address) return "";
        const parts = address.split(',');
        return parts.length > 0 ? parts[0].trim() : "";
    };

    // Helper function to get street from studio (check streetAndNumber first, then extract from address)
    const getStreetFromStudio = (studio) => {
        if (studio?.streetAndNumber) {
            return studio.streetAndNumber;
        }
        if (studio?.address) {
            return extractStreetFromAddress(studio.address);
        }
        return "";
    };

    // Initialize formData from studio, and update when studio changes
    const [formData, setFormData] = useState({
        country: studio?.country || "Deutschland",
        street: getStreetFromStudio(studio),
        additionalAddress: studio?.adressLine2 || "",
        postalCode: studio?.postcode || "",
        city: studio?.city || ""
    });

    // Update formData when studio prop changes
    useEffect(() => {
        if (studio) {
            const hasAddressUpdates = [
                "streetAndNumber",
                "adressLine2",
                "postcode",
                "city",
                "country",
                "latitude",
                "longitude",
            ].some(hasUpdatedField);
            const streetValue = getUpdatedField("streetAndNumber", getStreetFromStudio(studio));
            setFormData({
                country: getUpdatedField("country", studio.country || "Deutschland"),
                street: streetValue,
                additionalAddress: getUpdatedField("adressLine2", studio.adressLine2 || ""),
                postalCode: getUpdatedField("postcode", studio.postcode || ""),
                city: getUpdatedField("city", studio.city || "")
            });
            // Also update streetInputValue
            setStreetInputValue(streetValue);
            hasAddressChangesRef.current = hasAddressUpdates;
        }
        // Rehydrate from updatedFields on remount; user input updates local state directly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studio]);

    // Initialize streetInputValue when formData changes externally
    useEffect(() => {
        const newStreetValue = formData.street || "";
        if (newStreetValue !== streetInputValue && !showSuggestions) {
            setStreetInputValue(newStreetValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.street, showSuggestions]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        hasAddressChangesRef.current = true;
        
        // Special handling for street input - trigger autocomplete
        if (name === "street") {
            const inputValue = value || "";
            setStreetInputValue(inputValue);
            if (inputValue.trim().length >= 3) {
                searchAddress(inputValue);
                setShowSuggestions(true);
            } else {
                clearSuggestions();
                setShowSuggestions(false);
            }
        }
        
        const newFormData = {
            ...formData,
            [name]: value
        };
        setFormData(newFormData);
        
        // Map form fields to API fields
        const apiFieldMapping = {
            street: 'streetAndNumber',
            additionalAddress: 'adressLine2',
            postalCode: 'postcode',
            city: 'city',
            country: 'country'
        };
        
        // Update the editor store with the new data
        updateField(apiFieldMapping[name], value);
    };

    // Handle selection from autocomplete suggestions
    const handleSuggestionSelect = async (placeId) => {
        try {
            const addressData = await selectPlace(placeId);
            
            const newFormData = {
                street: addressData.street || formData.street,
                postalCode: addressData.postalCode || formData.postalCode,
                city: addressData.city || formData.city,
                additionalAddress: addressData.additionalAddress || formData.additionalAddress,
                country: addressData.country || formData.country
            };
            
            setFormData(newFormData);
            setStreetInputValue(newFormData.street);
            setShowSuggestions(false);
            clearSuggestions();
            hasAddressChangesRef.current = true;
            
            // Update the editor store with new data
            updateField('streetAndNumber', newFormData.street);
            updateField('postcode', newFormData.postalCode);
            updateField('city', newFormData.city);
            updateField('adressLine2', newFormData.additionalAddress);
            updateField('country', newFormData.country);
            
            // Store coordinates if available
            if (addressData.coordinates) {
                updateField('latitude', addressData.coordinates.lat);
                updateField('longitude', addressData.coordinates.lng);
            }
        } catch (error) {
            console.error('Error selecting place:', error);
        }
    };

    // Handle clicks outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                addressInputRef.current &&
                !addressInputRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleValidation = async () => {
        setIsValidating(true);
        
        const streetValue = getUpdatedField("streetAndNumber", formData.street);
        const postalCodeValue = getUpdatedField("postcode", formData.postalCode);
        const cityValue = getUpdatedField("city", formData.city);
        const countryValue = getUpdatedField("country", formData.country);
        
        // First, validate all fields locally
        const validationInput = {
            country: countryValue,
            street: streetValue,
            postalCode: postalCodeValue,
            city: cityValue
        };
        
        const fieldErrors = validateAddressFields(validationInput);
        
        // If there are any field-level errors, show them and stop
        if (Object.values(fieldErrors).some(error => error !== "")) {
            setErrors({ ...fieldErrors, general: "" });
            setIsValidating(false);
            return false;
        }
        
        // All field validations passed, now verify with Google API
        try {
            const googleValidation = await validateAddressWithGoogle({
                street: streetValue,
                postalCode: postalCodeValue,
                city: cityValue,
                country: countryValue
            });
            
            if (!googleValidation.isValid) {
                setErrors({
                    country: "",
                    street: "",
                    postalCode: "",
                    city: "",
                    general: googleValidation.error
                });
                setIsValidating(false);
                return false;
            }
            
            // Use normalized values from Google API (official formatting)
            const normalizedStreet = googleValidation.normalizedStreet || streetValue;
            const normalizedCity = googleValidation.normalizedCity || cityValue;
            
            // Update form data with normalized values
            const normalizedFormData = {
                ...formData,
                street: normalizedStreet,
                city: normalizedCity
            };
            setFormData(normalizedFormData);
            
            if (hasAddressChangesRef.current) {
                // Update the editor store with normalized values
                updateField('streetAndNumber', normalizedStreet);
                updateField('postcode', postalCodeValue);
                updateField('city', normalizedCity);
                updateField('country', countryValue);

                // Store coordinates if they were returned
                if (googleValidation.coordinates) {
                    updateField('latitude', googleValidation.coordinates.lat);
                    updateField('longitude', googleValidation.coordinates.lng);
                }
            }
            
            setErrors({
                country: "",
                street: "",
                postalCode: "",
                city: "",
                general: ""
            });
            setIsValidating(false);
            return true;
            
        } catch (error) {
            setIsValidating(false);
            return false;
        }
    };

    useEffect(() => {
        if (validate && validate.current) {
            validate.current = handleValidation;
        }
    }, [validate, formData, updatedFields]);

    return (
        <div className={classes.mainContainer}>
            <div className={classes.titleContainer}>
                <h2 className={`${classes.title} titleSemibold`}>Wo befindet sich dein Studio?</h2>
            </div>
            <div className={classes.bottomContainer}>
                {errors.general && (
                    <ErrorMessage style={{marginBottom: "20px", textAlign: "center"}}>
                        {errors.general}
                    </ErrorMessage>
                )}
                <div className={classes.countryContainer}>
                    <h2>Land</h2>
                    <div className={classes.countryInput}>
                        <input
                            className="slimRegularLarge"
                            placeholder="Deutschland"
                            value={formData.country}
                            readOnly
                        />
                        <img src={GermanyFlag} alt="Flag of Germany" className={classes.flagIcon} />
                    </div>
                    {errors.country && <ErrorMessage style={{marginTop: "20px"}}>{errors.country}</ErrorMessage>}
                </div>
                <div className={classes.addressContainer}>
                    <h2>Adresse</h2>
                    <div className={`${classes.inputsContainer}`}>
                        <div className={classes.autocompleteWrapper} ref={addressInputRef}>
                            <input
                                className="slimRegularLargeText"
                                placeholder="Straße und Hausnummer *"
                                name="street"
                                value={streetInputValue || ""}
                                onChange={handleInputChange}
                                onFocus={() => {
                                    if (streetInputValue && streetInputValue.trim().length >= 3) {
                                        searchAddress(streetInputValue);
                                        setShowSuggestions(true);
                                    }
                                }}
                                disabled={isValidating}
                            />
                            {showSuggestions && suggestions.length > 0 && (
                                <div className={classes.suggestionsContainer} ref={suggestionsRef}>
                                    {isLoading && (
                                        <div className={classes.suggestionItem}>Lädt...</div>
                                    )}
                                    {suggestions.map((suggestion) => (
                                        <div
                                            key={suggestion.place_id}
                                            className={classes.suggestionItem}
                                            onClick={() => handleSuggestionSelect(suggestion.place_id)}
                                        >
                                            {suggestion.description}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {errors.street && <ErrorMessage>{errors.street}</ErrorMessage>}
                        <input
                            className="slimRegularLarge"
                            placeholder="Adresszusatz: Stockwerk, Gebäude"
                            name="additionalAddress"
                            value={formData.additionalAddress}
                            onChange={handleInputChange}
                            disabled={isValidating}
                        />
                        <input
                            className="slimRegularLarge"
                            placeholder="Postleitzahl *"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleInputChange}
                            disabled={isValidating}
                        />
                        {errors.postalCode && <ErrorMessage>{errors.postalCode}</ErrorMessage>}
                        <input
                            className="slimRegularLarge"
                            placeholder="Stadt/Ort *"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            disabled={isValidating}
                        />
                        {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StandortEditor;
